/**
 * /admin — list of every Kin user with their global role. Change a role via
 * the dropdown next to each row. Seed admins are hardcoded as admin and
 * their dropdown is disabled (the backend refuses to demote them anyway).
 */

import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import type { AdminUserRow } from '../../api/types.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import { Avatar } from '../../components/Avatar.js';
import {
  Banner,
  Card,
  CardHeader,
  PageHeader,
  PageSkeleton,
  Spinner,
  inputClass,
} from '../../components/ui.js';
import { relTime } from '../../lib/format.js';

type Role = 'platform_admin' | 'user';

interface State {
  loading: boolean;
  users?: AdminUserRow[];
  error?: string;
}

export function AdminUsers() {
  const [state, setState] = useState<State>({ loading: true });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    tone: 'info' | 'success' | 'error';
    text: string;
  } | null>(null);

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const res = await api.get<{ users: AdminUserRow[] }>('/api/admin/users');
      setState({ loading: false, users: res.users });
    } catch (err) {
      setState({
        loading: false,
        error: err instanceof ApiError ? err.message : String(err),
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function changeRole(user: AdminUserRow, nextRole: Role) {
    if (nextRole === user.global_role) return;
    if (
      !confirm(
        nextRole === 'platform_admin'
          ? `Promote ${user.email} to admin? They will see this page and the full Kin catalogue.`
          : `Demote ${user.email} from admin?`,
      )
    ) {
      return;
    }
    setBusyId(user.id);
    setMessage(null);
    try {
      await api.patch(`/api/admin/users/${encodeURIComponent(user.id)}/role`, { role: nextRole });
      setMessage({
        tone: 'success',
        text:
          nextRole === 'platform_admin'
            ? `${user.email} is now an admin.`
            : `${user.email} is no longer an admin.`,
      });
      await load();
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusyId(null);
    }
  }

  if (state.loading) return <PageSkeleton rows={5} />;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin"
        title="Users"
        description="Everyone who has signed into Kin. Promote a teammate to admin to give them access to this section."
      />

      <AdminTabs />

      {state.error && <Banner tone="error">{state.error}</Banner>}
      {message && (
        <div className="mb-5">
          <Banner tone={message.tone} onDismiss={() => setMessage(null)}>
            {message.text}
          </Banner>
        </div>
      )}

      <Card>
        <CardHeader
          title={`${state.users?.length ?? 0} user${state.users?.length === 1 ? '' : 's'}`}
          description="Default role is User. Admins see this page and the full app catalogue."
        />
        <ul className="divide-y divide-line">
          {(state.users ?? []).map((u) => (
            <li key={u.id} className="px-5 py-3 flex items-center justify-between gap-4">
              <div className="min-w-0 flex items-center gap-3">
                <Avatar user={u} size={32} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink truncate">{u.name ?? u.email}</div>
                  <div className="text-xs text-muted truncate">
                    {u.email}
                    <span className="text-faint"> · seen {relTime(u.last_seen_at)}</span>
                    <span className="text-faint">
                      {' · '}
                      {u.manager_count === 0
                        ? 'manages no apps'
                        : `manages ${u.manager_count} app${u.manager_count === 1 ? '' : 's'}`}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select
                  value={u.global_role}
                  onChange={(e) => void changeRole(u, e.target.value as Role)}
                  aria-label={`Role for ${u.email}`}
                  disabled={u.is_seed_admin || busyId !== null}
                  title={u.is_seed_admin ? 'Hardcoded admin — cannot be demoted' : undefined}
                  className={`${inputClass} !w-auto !py-1.5 text-xs`}
                >
                  <option value="user">User</option>
                  <option value="platform_admin">Admin</option>
                </select>
                {busyId === u.id && <Spinner size={14} />}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
